<template>
  <div
    :class="cn(variants({ color, size, border }), props.class)"
    :style="progressBackground"
  >
    <div class="flex items-center grow gap-3 overflow-hidden">
      <FileType
        variant="solid"
        :size="size"
        :extension="getExtension(file.name)"
      />
      <div class="flex flex-col grow overflow-hidden">
        <span class="text-secondary text-style-sm font-medium truncate">
          {{ file.name }}
        </span>
        <span v-if="file.size" class="text-tertiary text-style-sm">
          {{ formatBytes(file.size) }}
          <span v-if="loading && loadingProgress < 1">
            {{
              $t("components.file_input.upload_progress", {
                progress: loadingPercent,
              })
            }}
          </span>
        </span>
      </div>
    </div>
    <div v-if="!loading" class="flex items-center justify-between">
      <IconButton
        v-if="canDownload"
        variant="tertiary"
        type="gray"
        :size="size"
        @click="downloadFile(file.urls.download)"
      >
        <Icon name="download" size="xl" />
      </IconButton>
      <IconButton
        v-if="canPreview"
        variant="tertiary"
        type="gray"
        :size="size"
        @click="$emit('preview', file)"
      >
        <Icon name="eye-visible" size="xl" />
      </IconButton>
      <IconButton
        v-if="canRemove"
        variant="tertiary"
        type="gray"
        :size="size"
        @click="$emit('remove', file)"
      >
        <Icon name="delete" size="xl" />
      </IconButton>
    </div>
    <div v-else-if="loading && loadingProgress < 1">
      <LoadingIndicator
        :progress="loadingPercent"
        :type="maxSmall ? 'circle' : 'bar'"
        class="fg-brand-primary"
      />
    </div>
    <div v-else-if="loading && loadingProgress >= 1" class="px-2">
      <Icon name="check-circle" size="xl" class="fg-success-secondary" />
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { IconButton } from "@/SharedComponents/Buttons";
import { LoadingIndicator } from "@/SharedComponents/LoadingIndicator";
import { Icon } from "@/SharedComponents/Icon";
import { FileType } from "@/SharedComponents/File";
import { variants } from "@/SharedComponents/Files";
import { useWebView } from "@/Composables/useWebView";
import { mobileSatisfiesVersion } from "@/Utils/version";
import { getExtension } from "@/Utils/files";
import { formatBytes } from "@/Utils/size";
import { cn } from "@/Utils/cn";
import { useBreakpoints } from "@/Composables/useBreakpoints";

defineEmits(["preview", "remove"]);

const props = defineProps({
  file: { type: Object, required: true },
  color: { type: String, required: false },
  size: { type: String, required: false },
  border: { type: Boolean, default: undefined },
  download: { type: Boolean, required: false },
  preview: { type: Boolean, required: false },
  remove: { type: Boolean, required: false },
  class: { type: null, required: false },
  loadingProgress: { type: Number, default: 0 },
  loading: { type: Boolean, default: false },
});

const { maxSmall } = useBreakpoints();

const canDownload = computed(
  () => (props.download || props.file.download) && props.file.urls?.download
);

const canPreview = computed(
  () =>
    (props.preview || props.file.preview) &&
    (props.file.urls?.file || props.file.url)
);

const canRemove = computed(() => props.remove || props.file.remove);

const loadingPercent = computed(() => Math.trunc(props.loadingProgress * 100));

const progressBackground = computed(() => {
  if (!maxSmall) return {};
  if (props.loading && props.loadingProgress < 1) {
    return {
      background: `linear-gradient(90deg, var(--bg-secondary) 0%, var(--bg-secondary) ${loadingPercent.value}%, transparent ${loadingPercent.value}%, transparent 100%)`,
    };
  }
  return {};
});

const { runningInWebView, openURL } = useWebView();

function downloadFile(url) {
  if (!runningInWebView()) {
    window.open(url, "_blank");
    return;
  }

  if (mobileSatisfiesVersion("2.2.0")) {
    openURL(url);
    return;
  }

  // Fallback for outdated mobile apps
  window.open(url, "_blank");
}
</script>
